/**
 * Estado do interruptor "Modo admin" da sessão de revisão: visão, nunca
 * permissão (design.md, Decisão 7; proposal.md, S9). O gate real continua
 * `hasProfile(profile, ADMIN)`, avaliado pelo chamador antes de renderizar
 * qualquer coisa deste módulo — este arquivo não sabe o que é um perfil e
 * não decide acesso. Desligar o interruptor só esconde peças já liberadas
 * pelo perfil; nenhum código deste módulo é, nem deveria ser, lido para
 * decidir o que alguém pode fazer.
 *
 * Uma chave só de `localStorage`, lida antes da primeira pintura — a mesma
 * semente que `useProfileStore` já usa para o perfil — para a tela nunca
 * nascer num estado e piscar no outro.
 */
import { useCallback, useState } from 'react';

export const CHAVE_MODO_ADMIN = 'revisao.modoAdmin';

/** Texto único do rótulo — quem renderiza e quem testa leem daqui, nunca duplicam a string. */
export const ROTULO_MODO_ADMIN = 'Modo admin';

function lerArmazenado(): boolean | null {
  try {
    const raw = localStorage.getItem(CHAVE_MODO_ADMIN);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return null;
  } catch {
    // localStorage pode lançar (modo privado, cookies bloqueados) — sem
    // semente, o caso é o mesmo de "nada gravado ainda".
    return null;
  }
}

function gravarArmazenado(ligado: boolean): void {
  try {
    localStorage.setItem(CHAVE_MODO_ADMIN, String(ligado));
  } catch {
    /* idem */
  }
}

/**
 * Nasce ligado na primeira sessão de quem administra — nada gravado ainda é
 * o mesmo caso que "ligado" (spec, "Ligado por padrão para quem administra").
 * Lida de forma síncrona para alimentar o `useState` inicial sem um efeito,
 * e portanto sem o quadro de "pisca num estado antes de assentar no outro".
 */
export function modoAdminSemente(): boolean {
  return lerArmazenado() ?? true;
}

/**
 * Estado do interruptor, para uma tela que já confirmou que quem está
 * presente administra. Chame este hook sempre — nunca atrás de
 * `if (administra)` — porque `administra` pode passar de falso para
 * verdadeiro quando o perfil termina de carregar, e um hook não pode nascer
 * condicional. Quem decide o que aparece na tela é quem chama, não este
 * hook: use `ligado` no JSX só onde `administra` também for verdadeiro.
 */
export function useModoAdminSessao(): { ligado: boolean; alternar: () => void } {
  const [ligado, setLigado] = useState(modoAdminSemente);
  const alternar = useCallback(() => {
    setLigado((atual) => {
      const proximo = !atual;
      gravarArmazenado(proximo);
      return proximo;
    });
  }, []);
  return { ligado, alternar };
}
